import React, { useState, useEffect } from 'react';
import { Icon } from '@iconify/react';
import PaymentPage from '../PaymentPage';
import { useTranslation } from 'react-i18next';

export default function RiskManagementModal({ 
  isOpen, 
  onClose, 
  onAccept, 
  offerTitle, 
  offerPrice 
}) {
  const { t } = useTranslation();
  const [isAccepted, setIsAccepted] = useState(false);
  const [showPayment, setShowPayment] = useState(false);

  const handleAccept = () => {
    if (isAccepted) {
      setShowPayment(true);
    }
  };

  const handlePaymentClose = () => {
    setShowPayment(false);
    onClose();
  };

  const handleClose = () => {
    setIsAccepted(false);
    onClose();
  };

  // Gérer la fermeture avec la touche Échap
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      // Empêcher le scroll du body quand la modale est ouverte
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  if (showPayment) {
    return (
      <PaymentPage 
        onClose={handlePaymentClose}
        pricingData={{
          title: offerTitle,
          price: offerPrice
        }}
      />
    );
  }

  return (
    <div 
      className="modal-overlay" 
      onClick={handleClose}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        padding: '20px'
      }}
    >
      <div 
        className="modal-content" 
        onClick={(e) => e.stopPropagation()}
        style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '40px',
          maxWidth: '600px',
          width: '100%',
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'relative',
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)'
        }}
      >
        {/* Bouton de fermeture */}
        <button 
          onClick={handleClose}
          style={{
            position: 'absolute',
            top: '15px',
            right: '15px',
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#666',
            padding: '5px',
            borderRadius: '50%',
            width: '35px',
            height: '35px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Icon icon="material-symbols:close" />
        </button>

        {/* Informations sur l'offre */}
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '15px',
          padding: '20px',
          marginBottom: '25px',
          border: '2px solid #e9ecef'
        }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '10px'
          }}>
            {offerTitle}
          </h3>
          <p style={{
            fontSize: '18px',
            color: '#007bff',
            fontWeight: 'bold',
            margin: 0
          }}>
            {offerPrice}
          </p>
        </div>

        {/* Conditions importantes */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            fontSize: '20px',
            fontWeight: 'bold',
            color: '#333',
            marginBottom: '20px',
            display: 'flex',
            alignItems: 'center'
          }}>
            <Icon 
              icon="material-symbols:info" 
              style={{ marginRight: '10px', color: '#007bff' }}
            />
            {t('subscription.modal.title')}
          </h3>
          
          <div style={{ marginBottom: '20px' }}>
            <div style={{
              backgroundColor: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '15px'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#856404',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Icon 
                  icon="material-symbols:money-off" 
                  style={{ marginRight: '8px' }}
                />
                {t('subscription.modal.nonRefundable.title')}
              </h4>
              <p style={{
                fontSize: '14px',
                color: '#856404',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {t('subscription.modal.nonRefundable.text')}
              </p>
            </div>

            <div style={{
              backgroundColor: '#d1ecf1',
              border: '1px solid #bee5eb',
              borderRadius: '10px',
              padding: '15px',
              marginBottom: '15px'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#0c5460',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Icon 
                  icon="material-symbols:event-available" 
                  style={{ marginRight: '8px' }}
                />
                {t('subscription.modal.cancellation.title')}
              </h4>
              <p style={{
                fontSize: '14px',
                color: '#0c5460',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {t('subscription.modal.cancellation.text')}
              </p>
            </div>

            <div style={{
              backgroundColor: '#f8d7da',
              border: '1px solid #f5c6cb',
              borderRadius: '10px',
              padding: '15px'
            }}>
              <h4 style={{
                fontSize: '16px',
                fontWeight: 'bold',
                color: '#721c24',
                marginBottom: '8px',
                display: 'flex',
                alignItems: 'center'
              }}>
                <Icon 
                  icon="material-symbols:gavel" 
                  style={{ marginRight: '8px' }}
                />
                {t('subscription.modal.commitment.title')}
              </h4>
              <p style={{
                fontSize: '14px',
                color: '#721c24',
                margin: 0,
                lineHeight: '1.5'
              }}>
                {t('subscription.modal.commitment.text')}
              </p>
            </div>
          </div>
        </div>

        {/* Checkbox d'acceptation */}
        <div style={{
          backgroundColor: '#f8f9fa',
          borderRadius: '10px',
          padding: '20px',
          marginBottom: '25px',
          border: '2px solid #e9ecef'
        }}>
          <label style={{
            display: 'flex',
            alignItems: 'flex-start',
            cursor: 'pointer',
            fontSize: '16px',
            lineHeight: '1.5'
          }}>
            <input
              type="checkbox"
              checked={isAccepted}
              onChange={(e) => setIsAccepted(e.target.checked)}
              style={{
                marginRight: '12px',
                marginTop: '2px',
                transform: 'scale(1.2)',
                accentColor: '#007bff'
              }}
            />
            <span style={{ color: '#333' }}>
              {t('subscription.modal.acceptLabel')}
            </span>
          </label>
        </div>

        {/* Boutons d'action */}
        <div style={{
          display: 'flex',
          gap: '15px',
          justifyContent: 'center'
        }}>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              padding: '12px 30px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: 'pointer',
              transition: 'all 0.3s ease',
              minWidth: '120px'
            }}
            onMouseOver={(e) => e.target.style.backgroundColor = '#5a6268'}
            onMouseOut={(e) => e.target.style.backgroundColor = '#6c757d'}
          >
            {t('subscription.modal.cancel')}
          </button>
          <button
            onClick={handleAccept}
            disabled={!isAccepted}
            style={{
              backgroundColor: isAccepted ? '#28a745' : '#6c757d',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              padding: '12px 30px',
              fontSize: '16px',
              fontWeight: 'bold',
              cursor: isAccepted ? 'pointer' : 'not-allowed',
              transition: 'all 0.3s ease',
              minWidth: '120px',
              opacity: isAccepted ? 1 : 0.6
            }}
            onMouseOver={(e) => {
              if (isAccepted) {
                e.target.style.backgroundColor = '#218838';
              }
            }}
            onMouseOut={(e) => {
              if (isAccepted) {
                e.target.style.backgroundColor = '#28a745';
              }
            }}
          >
            {t('subscription.modal.proceed')}
          </button>
        </div>
      </div>
    </div>
  );
}
